/**
 * Invoice Version History Component
 *
 * Displays version history for an invoice in a timeline layout,
 * with side-by-side diff view when opening a version.
 *
 * NOTE: Snapshots come from the server as snake_case JSONB maps.
 * The InvoiceVersion type says Partial<Invoice> (camelCase) but
 * the actual wire data uses snake_case keys — use RawInvoiceSnapshot.
 */

import { useState, useEffect } from 'react'

import { useBaseCurrency } from '@/hooks/use-currencies'
import { getInvoiceVersions, getInvoiceVersion } from '@/services/invoice-service'
import type { InvoiceVersion } from '@/types/billing'

import { VersionDetailView } from './VersionDetailView'
import { VersionTimeline } from './VersionTimeline'

import type { RawVersion } from './invoice-version-types'

interface InvoiceVersionHistoryProps {
  orgId: string
  invoiceId: string
  isOpen: boolean
  onClose: () => void
}

function useVersionData(orgId: string, invoiceId: string, isOpen: boolean): {
  versions: InvoiceVersion[]
  isLoading: boolean
  selectedVersion: RawVersion | null
  compareVersion: RawVersion | null
  isCompareLoading: boolean
  viewVersion: (vn: number) => void
  changeCompare: (vn: number | null) => void
  resetSelection: () => void
} {
  const [versions, setVersions] = useState<InvoiceVersion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedVersion, setSelectedVersion] = useState<RawVersion | null>(null)
  const [compareVersion, setCompareVersion] = useState<RawVersion | null>(null)
  const [isCompareLoading, setIsCompareLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) { return }
    setIsLoading(true)
    void getInvoiceVersions(orgId, invoiceId)
      .then(r => { setVersions(r.data?.versions ?? []) })
      .catch(() => { /* silently fail */ })
      .finally(() => { setIsLoading(false) })
    return () => { setSelectedVersion(null); setCompareVersion(null) }
  }, [isOpen, invoiceId, orgId])

  const viewVersion = (vn: number): void => {
    const sorted = [...versions].sort((a, b) => b.versionNumber - a.versionNumber)
    const compareNum = sorted.find(v => v.versionNumber < vn)?.versionNumber
    const reqs = [
      getInvoiceVersion(orgId, invoiceId, vn),
      ...(compareNum !== undefined ? [getInvoiceVersion(orgId, invoiceId, compareNum)] : []),
    ]
    void Promise.all(reqs)
      .then(([cur, cmp]) => {
        setSelectedVersion((cur?.data?.version as unknown as RawVersion) ?? null)
        setCompareVersion((cmp?.data?.version as unknown as RawVersion) ?? null)
      })
      .catch(() => { /* silently fail */ })
  }

  const changeCompare = (vn: number | null): void => {
    if (vn === null) { setCompareVersion(null); return }
    setIsCompareLoading(true)
    void getInvoiceVersion(orgId, invoiceId, vn)
      .then(r => { setCompareVersion((r.data?.version as unknown as RawVersion) ?? null) })
      .catch(() => { /* silently fail */ })
      .finally(() => { setIsCompareLoading(false) })
  }

  const resetSelection = (): void => { setSelectedVersion(null); setCompareVersion(null) }

  return { versions, isLoading, selectedVersion, compareVersion, isCompareLoading, viewVersion, changeCompare, resetSelection }
}

export function InvoiceVersionHistory({
  orgId, invoiceId, isOpen, onClose,
}: InvoiceVersionHistoryProps): JSX.Element | null {
  const vd = useVersionData(orgId, invoiceId, isOpen)
  const baseCurrency = useBaseCurrency()

  if (!isOpen) { return null }

  if (vd.selectedVersion) {
    return (
      <VersionDetailView
        baseCurrency={baseCurrency}
        compareVersion={vd.compareVersion}
        isCompareLoading={vd.isCompareLoading}
        selectedVersion={vd.selectedVersion}
        versions={vd.versions}
        onBack={vd.resetSelection}
        onChangeCompare={vd.changeCompare}
        onClose={() => { vd.resetSelection(); onClose() }}
      />
    )
  }

  return (
    <VersionTimeline
      baseCurrency={baseCurrency}
      isLoading={vd.isLoading}
      isOpen={isOpen}
      versions={vd.versions}
      onClose={() => { vd.resetSelection(); onClose() }}
      onViewVersion={vd.viewVersion}
    />
  )
}

export default InvoiceVersionHistory
